/* Section behaviours that must survive theme-editor rerenders.
   Shopify does not re-run a section's inline scripts after a rerender, so
   anything a rerendered or newly added section needs lives here: the
   slideshow initializer (re-run from shopify:section:load) and the
   delegated one-shot behaviours (video facade, footer localization).
   Emitting sections: sections/slideshow.liquid, sections/video.liquid,
   sections/footer.liquid. */
(function () {
  /* ---- slideshow ------------------------------------------------------- */
  function initSlideshow(root) {
    if (!root || root.dataset.slideshowInit === 'true') return;
    root.dataset.slideshowInit = 'true';

    var frames = Array.prototype.slice.call(root.querySelectorAll('[data-slide-frame]'));
    var copies = Array.prototype.slice.call(root.querySelectorAll('[data-slide-copy]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-slide-dot]'));
    if (frames.length < 2) return;

    var index = 0;
    var timer = null;
    var autoplay = root.dataset.autoplay === 'true';
    var autoplayMs = (parseFloat(root.dataset.autoplaySeconds) || 5) * 1000;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    /* Sideways movement (sections/slideshow.liquid CSS). next above the current slide moves forward: the
       current pair leaves to the left and the new pair enters from the right. Below it moves back. The raw
       next is compared before wrapping, so last -> first on "next" still moves forward. */
    function setSide(el, side) {
      if (!el) return;
      if (side) el.setAttribute('data-slide-side', side);
      else el.removeAttribute('data-slide-side');
    }
    function show(next) {
      var previous = index;
      var forward = next > previous;
      index = ((next % frames.length) + frames.length) % frames.length;
      if (index !== previous) {
        var arriving = [frames[index], copies[index]];
        /* jump the arriving pair to the side it enters from, with no transition, then let it move in */
        arriving.forEach(function (el) {
          if (!el) return;
          el.classList.add('is-staging');
          setSide(el, forward ? 'after' : 'before');
        });
        void root.offsetWidth;
        arriving.forEach(function (el) { if (el) el.classList.remove('is-staging'); });
        setSide(frames[previous], forward ? 'before' : 'after');
        setSide(copies[previous], forward ? 'before' : 'after');
        arriving.forEach(function (el) { setSide(el, null); });
      }
      frames.forEach(function (frame, i) {
        frame.classList.toggle('is-active', i === index);
        /* inert keeps hidden slides out of the tab order and away from
           screen readers — moving them off-canvas hides them visually only */
        frame.toggleAttribute('inert', i !== index);
      });
      copies.forEach(function (copy, i) {
        copy.classList.toggle('is-active', i === index);
        copy.toggleAttribute('inert', i !== index);
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    var paused = false;
    /* keyboard focus inside the slideshow holds autoplay, so the focused slide is never made inert under it */
    var focusHeld = false;

    function stopTimer() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    function restartTimer() {
      stopTimer();
      /* isConnected: an editor rerender detaches this instance; its old
         document-level listeners must not restart a dead timer */
      if (!autoplay || paused || focusHeld || reducedMotion.matches || !root.isConnected || document.hidden) return;
      timer = setInterval(function () { show(index + 1); }, autoplayMs);
    }

    /* :focus-visible limits the hold to keyboard focus; a mouse click on a dot or arrow keeps autoplay as before.
       Browsers without :focus-visible throw on the selector, so they hold on any focus. */
    function isKeyboardFocus(el) {
      try { return el.matches(':focus-visible'); } catch (err) { return true; }
    }
    root.addEventListener('focusin', function (e) {
      if (!isKeyboardFocus(e.target)) return;
      focusHeld = true;
      stopTimer();
    });
    root.addEventListener('focusout', function (e) {
      if (!focusHeld || (e.relatedTarget && root.contains(e.relatedTarget))) return;
      focusHeld = false;
      restartTimer();
    });

    var pauseButton = root.querySelector('[data-slide-pause]');
    if (pauseButton) {
      pauseButton.addEventListener('click', function () {
        paused = !paused;
        pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
        pauseButton.setAttribute('aria-label', paused ? pauseButton.dataset.labelPlay : pauseButton.dataset.labelPause);
        pauseButton.innerHTML = paused ? '&#9654;' : '&#10074;&#10074;';
        restartTimer();
      });
    }

    /* a hidden tab must not keep advancing slides */
    function onVisibility() {
      if (document.hidden) stopTimer();
      else restartTimer();
    }
    document.addEventListener('visibilitychange', onVisibility);

    root.querySelectorAll('[data-slide-prev]').forEach(function (button) {
      button.addEventListener('click', function () { show(index - 1); restartTimer(); });
    });
    root.querySelectorAll('[data-slide-next]').forEach(function (button) {
      button.addEventListener('click', function () { show(index + 1); restartTimer(); });
    });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { show(i); restartTimer(); });
    });

    /* swipe (touch) + drag (mouse) */
    var startX = null, startY = null;
    function endSwipe(x, y) {
      if (startX === null) return;
      var dx = x - startX, dy = y - startY;
      startX = null;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        show(index + (dx < 0 ? 1 : -1));
        restartTimer();
      }
    }
    root.addEventListener('touchstart', function (e) {
      var touch = e.touches && e.touches[0];
      if (touch) { startX = touch.clientX; startY = touch.clientY; }
    }, { passive: true });
    root.addEventListener('touchend', function (e) {
      var touch = e.changedTouches && e.changedTouches[0];
      if (touch) endSwipe(touch.clientX, touch.clientY);
    });
    root.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      startX = e.clientX; startY = e.clientY;
    });
    root.addEventListener('pointerup', function (e) {
      if (e.pointerType === 'touch') return;
      endSwipe(e.clientX, e.clientY);
    });

    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', restartTimer);

    /* The waiting slides sit off-canvas, where a lazy image may not load until it starts to slide in.
       Load them all once the section is near the viewport, as the stacked crossfade frames used to. */
    function loadAllFrames() {
      root.querySelectorAll('[data-slide-frame] img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
    }
    if ('IntersectionObserver' in window) {
      var nearWatch = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        loadAllFrames();
        nearWatch.disconnect();
      }, { rootMargin: '300px 0px' });
      nearWatch.observe(root);
    } else {
      loadAllFrames();
    }

    /* instance API for the editor listeners below, plus teardown so a
       section unload removes every document-level listener */
    root.__slideshow = {
      show: show,
      stop: stopTimer,
      restart: restartTimer,
      frames: frames,
      teardown: function () {
        stopTimer();
        if (nearWatch) nearWatch.disconnect();
        document.removeEventListener('visibilitychange', onVisibility);
        if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', restartTimer);
      }
    };

    restartTimer();
  }

  function initAllSlideshows(scope) {
    (scope || document).querySelectorAll('section.slideshow').forEach(initSlideshow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAllSlideshows(); });
  } else {
    initAllSlideshows();
  }

  /* theme editor: Shopify inserts a rerendered section's HTML without
     executing its inline scripts. Re-create each script element so the
     section's own initializer runs again against the fresh DOM (a script
     inserted via createElement does execute). This revives the product
     page, recently-viewed, recommendations and popup after a rerender.
     Old document-level listeners from the previous run keep guards on
     their detached roots, so the duplicates are inert; the leak is
     bounded by the editor session. Fires only in the editor. */
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('script').forEach(function (old) {
      var fresh = document.createElement('script');
      /* copy attributes so a type="application/ld+json" block stays data
         instead of executing as JavaScript */
      Array.prototype.forEach.call(old.attributes, function (attr) {
        fresh.setAttribute(attr.name, attr.value);
      });
      if (!old.src) fresh.textContent = old.textContent;
      old.replaceWith(fresh);
    });
  });

  /* For block events e.target is the block element, so search upward first. */
  function slideshowFrom(e) {
    return e.target.closest('section.slideshow') || e.target.querySelector('section.slideshow');
  }
  document.addEventListener('shopify:section:load', function (e) {
    initAllSlideshows(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = slideshowFrom(e);
    if (el && el.__slideshow) el.__slideshow.teardown();
  });
  /* jump to the selected slide, hold while it is selected */
  document.addEventListener('shopify:block:select', function (e) {
    var el = slideshowFrom(e);
    if (!el || !el.__slideshow) return;
    var frame = el.querySelector('[data-slide-frame][data-block-id="' + e.detail.blockId + '"]');
    if (!frame) return;
    el.__slideshow.show(el.__slideshow.frames.indexOf(frame));
    el.__slideshow.stop();
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var el = slideshowFrom(e);
    if (el && el.__slideshow) el.__slideshow.restart();
  });

  /* ---- video section: click-to-load embed facade ------------------------ */
  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('[data-vid-play]');
    if (!button) return;
    var frame = button.closest('[data-vid-embed]');
    var template = frame && frame.querySelector('template[data-vid-iframe]');
    if (!template) return;
    frame.appendChild(template.content.cloneNode(true));
    button.remove();
    /* keep keyboard focus on the player, not the document body */
    var iframe = frame.querySelector('iframe');
    if (iframe) iframe.focus();
  });

  /* ---- footer localization: submit on change ---------------------------- */
  document.addEventListener('change', function (e) {
    var select = e.target.closest && e.target.closest('select[data-loc-submit]');
    if (select && select.form) select.form.submit();
  });
})();
